/**
 * Wholesale product modal — reusable across Lookbook and Order (later
 * steps). This file only implements the modal itself: fetching a
 * product's wholesale content, the finish-selector/gallery, quantity/line
 * totals, and cart integration via this theme's existing native events.
 * Nothing here connects it to any click trigger yet.
 */
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CONTENT_SECTION_ID = 'wholesale-product-modal-content';

/**
 * Like document.getElementById, but when more than one element shares the
 * id — which happens on the combined WS Portal page, where each of its
 * Lookbook/Order tab sections embeds its own copy of this modal shell via
 * {% render 'wholesale-product-modal' %} — this returns whichever copy
 * isn't sitting inside the OTHER tab's currently display:none section,
 * rather than always the first one in document order regardless of
 * whether it can actually render. Calling .showDialog() on a dialog
 * nested inside a hidden ancestor is exactly what caused the reported
 * bug: the dialog can never visually appear, but its scroll-lock behavior
 * still engages, freezing the page with no visible modal.
 *
 * Behaves identically to plain getElementById when there's only one
 * match — i.e. always, on both standalone WS Lookbook/WS Order pages.
 */
function getActiveElementById(id) {
  const matches = document.querySelectorAll('[id="' + id + '"]');
  if (matches.length <= 1) return matches[0] || null;
  for (const el of matches) {
    let hidden = false;
    let node = el.parentElement;
    while (node) {
      // A <dialog> that hasn't been opened yet is display:none by the
      // browser's own default stylesheet — that's unrelated to which tab
      // section it's in, and checking it here would make every candidate
      // (dialog not open yet, by definition, at the point this runs)
      // look "hidden", always falling back to the first match regardless
      // of which tab is actually active. Only real ancestor elements
      // (the tab sections themselves) count here.
      if (node.tagName !== 'DIALOG' && getComputedStyle(node).display === 'none') {
        hidden = true;
        break;
      }
      node = node.parentElement;
    }
    if (!hidden) return el;
  }
  return matches[0];
}

/**
 * Optional per-open adapter that redirects quantity changes away from the
 * real Shopify cart (used by pages with their own local order state, e.g.
 * WS Order — see assets/ws-order.js) instead of writing to /cart/update.js.
 * Set fresh on every open() call; null means "use the real cart" (the
 * original, Lookbook behavior — unchanged when no adapter is passed).
 * @type {{ onQuantityChange?: (variantId: string, quantity: number) => void, getQuantity?: (variantId: string) => number | undefined } | null}
 */
let activeAdapter = null;

class WholesaleProductDialog extends DialogComponent {}

if (!customElements.get('wholesale-product-dialog')) {
  customElements.define('wholesale-product-dialog', WholesaleProductDialog);
}

class WholesalePhotoZoomDialog extends DialogComponent {}

if (!customElements.get('wholesale-photo-zoom-dialog')) {
  customElements.define('wholesale-photo-zoom-dialog', WholesalePhotoZoomDialog);
}

bindZoomDialogControls();

/**
 * Clicking any gallery photo opens it larger in a separate, lightweight
 * zoom dialog (defined once in the shell, reused across every product).
 * The zoom dialog mirrors the SAME gallery state as the main modal (see
 * syncZoomView below) rather than keeping a separate copy, so navigating
 * or switching finish from inside the zoom view updates the main gallery
 * too, and vice versa.
 */
function bindGalleryZoom(root) {
  const zoomDialogEl = getActiveElementById('wholesale-photo-zoom-dialog');
  if (!(zoomDialogEl instanceof WholesalePhotoZoomDialog)) return;

  // On the combined WS Portal page there are two zoom dialogs (one per
  // tab section) — the one bound at script load (below) is only ever the
  // one visible at that moment. Re-checking here, on every modal open,
  // makes sure whichever one is actually active now gets its own prev/
  // next controls bound too; bindZoomDialogControls is idempotent per
  // element so this is a no-op once a given dialog is already bound.
  bindZoomDialogControls(zoomDialogEl);

  root.querySelectorAll('.wholesale-modal__gallery-slide img').forEach((img) => {
    img.addEventListener('click', () => {
      syncZoomView(root);
      zoomDialogEl.showDialog();
    });
  });
}

/**
 * Binds a zoom dialog's own prev/next arrows once (the dialog markup is
 * static in the shell, reused across every product load). Looks up the
 * live content root at click time since the zoom dialog persists across
 * product loads while the main content is fetched fresh each time.
 *
 * Takes an optional explicit element — on the combined WS Portal page
 * there are two zoom dialogs (one per tab section), so the initial
 * call at script load (binding whichever is active then) isn't enough;
 * bindGalleryZoom also calls this on every modal open with the currently
 * active one. The bound-dataset flag keeps this idempotent regardless of
 * how many times it's called for the same element.
 */
function bindZoomDialogControls(explicitZoomDialogEl) {
  const zoomDialogEl = explicitZoomDialogEl || getActiveElementById('wholesale-photo-zoom-dialog');
  if (!(zoomDialogEl instanceof WholesalePhotoZoomDialog) || zoomDialogEl.dataset.controlsBound) return;
  zoomDialogEl.dataset.controlsBound = 'true';

  const prevButton = zoomDialogEl.querySelector('[data-zoom-prev]');
  const nextButton = zoomDialogEl.querySelector('[data-zoom-next]');

  prevButton?.addEventListener('click', () => {
    const liveRoot = getActiveElementById('wholesale-product-modal-content');
    if (liveRoot) stepGallerySlide(liveRoot, -1);
  });
  nextButton?.addEventListener('click', () => {
    const liveRoot = getActiveElementById('wholesale-product-modal-content');
    if (liveRoot) stepGallerySlide(liveRoot, 1);
  });
}

/**
 * Rebuilds the zoom dialog's image, finish-selector, and indicators to
 * mirror the main gallery's current state. Called after every state
 * change (finish switch, arrow step, indicator click) regardless of
 * whether it originated in the main gallery or the zoom dialog itself,
 * and it's harmless to call while the zoom dialog is closed.
 */
function syncZoomView(root) {
  const zoomDialogEl = getActiveElementById('wholesale-photo-zoom-dialog');
  if (!(zoomDialogEl instanceof WholesalePhotoZoomDialog)) return;

  const zoomImage = zoomDialogEl.refs?.zoomImage;
  const slides = getGallerySlides(root);
  const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
  const activeImage = slides[activeIndex]?.querySelector('img');

  if (zoomImage instanceof HTMLImageElement && activeImage instanceof HTMLImageElement) {
    zoomImage.src = activeImage.currentSrc || activeImage.src;
    zoomImage.srcset = activeImage.srcset || '';
    zoomImage.alt = activeImage.alt || '';
  }

  const zoomFinishEl = zoomDialogEl.querySelector('[data-zoom-finish-selector]');
  if (zoomFinishEl) {
    zoomFinishEl.innerHTML = '';
    root.querySelectorAll('.wholesale-modal__finish-swatch').forEach((swatch) => {
      const target = swatch.dataset.finishTarget;
      const clone = document.createElement('button');
      clone.type = 'button';
      clone.className = 'wholesale-photo-zoom__finish-swatch' + (swatch.classList.contains('is-active') ? ' is-active' : '');
      clone.style.backgroundImage = swatch.style.backgroundImage;
      clone.setAttribute('aria-label', swatch.getAttribute('aria-label') || '');
      clone.setAttribute('aria-selected', swatch.getAttribute('aria-selected') || 'false');
      clone.addEventListener('click', () => {
        // Deferred: syncZoomView rebuilds this button's own parent via
        // innerHTML, which would remove the clicked element from the DOM
        // mid-click. If that happens before the click event finishes
        // bubbling to the dialog's own "click outside closes it" listener
        // (assets/dialog.js), dialog.contains(event.target) reports false
        // and the dialog closes itself. Deferring the rebuild lets that
        // check run first, against the still-present original button.
        setTimeout(() => {
          const liveRoot = getActiveElementById('wholesale-product-modal-content');
          if (liveRoot) setActiveFinish(liveRoot, target);
        }, 0);
      });
      zoomFinishEl.appendChild(clone);
    });
  }

  const zoomIndicatorsEl = zoomDialogEl.querySelector('[data-zoom-indicators]');
  if (zoomIndicatorsEl) {
    zoomIndicatorsEl.innerHTML = '';
    slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'wholesale-photo-zoom__dot' + (index === activeIndex ? ' is-active' : '');
      dot.setAttribute('aria-label', `Go to image ${index + 1}`);
      dot.addEventListener('click', () => {
        // Deferred for the same reason as the finish-swatch clones above.
        setTimeout(() => {
          const liveRoot = getActiveElementById('wholesale-product-modal-content');
          if (liveRoot) setActiveSlideIndex(liveRoot, index);
        }, 0);
      });
      zoomIndicatorsEl.appendChild(dot);
    });
  }
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

/**
 * Fetches the wholesale content section for a given product URL via the
 * Section Rendering API. The section is never placed in any page
 * template — it only exists to be rendered this way, scoped to whichever
 * product handle is in the requested URL.
 * @param {string} productUrl
 * @returns {Promise<Element|null>}
 */
async function fetchProductModalContent(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('section_id', CONTENT_SECTION_ID);

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Failed to load wholesale product content: HTTP ${response.status}`);
  }

  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return parsed.querySelector('[data-wholesale-product-content]');
}

function getActiveGalleryGroup(root) {
  return root.querySelector('.wholesale-modal__gallery-group.is-active');
}

function getGallerySlides(root) {
  const group = getActiveGalleryGroup(root);
  return group ? Array.from(group.querySelectorAll('.wholesale-modal__gallery-slide')) : [];
}

function updateGalleryIndicators(root) {
  const indicatorsEl = root.querySelector('[data-gallery-indicators]');
  if (!indicatorsEl) return;

  const slides = getGallerySlides(root);
  const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));

  indicatorsEl.innerHTML = '';
  slides.forEach((slide, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'wholesale-modal__gallery-dot' + (index === activeIndex ? ' is-active' : '');
    dot.setAttribute('aria-label', `Go to image ${index + 1}`);
    dot.addEventListener('click', () => setActiveSlideIndex(root, index));
    indicatorsEl.appendChild(dot);
  });
}

/**
 * Single source of truth for "which image is showing" — used by the main
 * gallery's arrows/indicators AND the zoom dialog's arrows/indicators, so
 * both stay in sync no matter which one triggered the change.
 */
function setActiveSlideIndex(root, index) {
  const slides = getGallerySlides(root);
  if (slides.length === 0) return;

  const wrappedIndex = ((index % slides.length) + slides.length) % slides.length;
  slides.forEach((slide, i) => slide.classList.toggle('is-active', i === wrappedIndex));

  updateGalleryIndicators(root);
  syncZoomView(root);
}

function stepGallerySlide(root, direction) {
  const slides = getGallerySlides(root);
  if (slides.length === 0) return;

  const currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
  setActiveSlideIndex(root, currentIndex + direction);
}

/**
 * Single source of truth for "which finish is showing" — used by the main
 * gallery's finish swatches AND the zoom dialog's cloned finish swatches.
 * Only ever switches which product's images are shown; it never touches
 * the ordering rows below, which always list every variant from every
 * linked finish-product.
 */
function setActiveFinish(root, target) {
  root.querySelectorAll('.wholesale-modal__finish-swatch').forEach((swatch) => {
    const isActive = swatch.dataset.finishTarget === target;
    swatch.classList.toggle('is-active', isActive);
    swatch.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  root.querySelectorAll('.wholesale-modal__gallery-group').forEach((group) => {
    group.classList.toggle('is-active', group.dataset.galleryFor === target);
  });

  updateGalleryIndicators(root);
  syncZoomView(root);
}

function bindFinishSelector(root) {
  root.querySelectorAll('.wholesale-modal__finish-swatch').forEach((swatch) => {
    swatch.addEventListener('click', () => setActiveFinish(root, swatch.dataset.finishTarget));
  });

  root.querySelectorAll('.wholesale-modal__gallery-arrow--prev').forEach((btn) => {
    btn.addEventListener('click', () => stepGallerySlide(root, -1));
  });
  root.querySelectorAll('.wholesale-modal__gallery-arrow--next').forEach((btn) => {
    btn.addEventListener('click', () => stepGallerySlide(root, 1));
  });

  updateGalleryIndicators(root);
  syncZoomView(root);
}

/**
 * Recomputes every row's line total and the modal-wide running total from
 * the quantities currently entered — purely local math, no network call.
 * The actual cart write happens separately, only when a quantity changes.
 */
function updateTotals(root) {
  const rows = root.querySelectorAll('.wholesale-modal__row');
  let itemCount = 0;
  let totalCents = 0;

  rows.forEach((row) => {
    const input = row.querySelector('input[type="number"]');
    const quantity = parseInt(input?.value || '0', 10) || 0;
    const unitPriceCents = parseInt(row.dataset.unitPrice || '0', 10);
    const lineTotalCents = quantity * unitPriceCents;

    const lineTotalEl = row.querySelector('[data-line-total]');
    if (lineTotalEl) lineTotalEl.textContent = formatMoney(lineTotalCents);

    if (quantity > 0) {
      itemCount += quantity;
      totalCents += lineTotalCents;
    }
  });

  const countEl = root.querySelector('[data-cart-count]');
  const totalEl = root.querySelector('[data-cart-total]');
  if (countEl) countEl.textContent = `${itemCount} in cart`;
  if (totalEl) totalEl.textContent = formatMoney(totalCents);
}

/**
 * Sets a variant's cart line to an absolute quantity via the theme's
 * existing native cart endpoint, then dispatches CartAddEvent so any
 * existing listener (e.g. the cart drawer) reacts exactly as it already
 * does for every other add-to-cart path in this theme.
 */
async function updateCartQuantity(variantId, quantity, signal) {
  const body = JSON.stringify({ updates: { [variantId]: quantity } });
  const cartUpdateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';

  const response = await fetch(cartUpdateUrl, {
    ...fetchConfig('json', { body }),
    signal,
  });
  const data = await response.json();

  document.dispatchEvent(
    new CartAddEvent(data, 'wholesale-product-modal', {
      source: 'wholesale-product-modal',
      variantId: String(variantId),
      itemCount: data.item_count,
      didError: Boolean(data.errors),
    })
  );

  return data;
}

/**
 * Row/modal totals update instantly on every event (below), but the actual
 * cart write is debounced + abortable per variant — mirroring the same
 * debounce-plus-AbortController pattern this theme's own
 * assets/quick-order-list.js uses for quantity updates. Without it, rapid
 * +/- clicks fire overlapping requests whose responses can resolve out of
 * order, leaving the real cart total out of sync with what the row and
 * modal already show locally. Keyed per variant (rather than one shared
 * controller) so adjusting one row doesn't cancel another row's update.
 */
function bindOrderingRows(root) {
  /** @type {Map<string, { timer: ReturnType<typeof setTimeout>, controller: AbortController | null }>} */
  const pendingByVariant = new Map();

  root.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
    const row = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('.wholesale-modal__row') : null);
    if (!row) return;

    updateTotals(root);

    const variantId = row.dataset.variantId;
    const quantity = event.detail?.quantity ?? 0;
    if (!variantId) return;

    const pending = pendingByVariant.get(variantId);
    if (pending) {
      clearTimeout(pending.timer);
      pending.controller?.abort();
    }

    const timer = setTimeout(async () => {
      // Adapter path (e.g. WS Order): write to the caller's own local order
      // state instead of the real cart. Synchronous and local, so there's
      // nothing to abort — the debounce above still collapses rapid clicks
      // into a single call.
      if (activeAdapter?.onQuantityChange) {
        pendingByVariant.delete(variantId);
        activeAdapter.onQuantityChange(variantId, quantity);
        return;
      }

      const controller = new AbortController();
      pendingByVariant.set(variantId, { timer, controller });

      try {
        await updateCartQuantity(variantId, quantity, controller.signal);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Wholesale product modal: cart update failed', error);
        }
      } finally {
        if (pendingByVariant.get(variantId)?.controller === controller) {
          pendingByVariant.delete(variantId);
        }
      }
    }, 300);

    pendingByVariant.set(variantId, { timer, controller: null });
  });

  updateTotals(root);
}

/**
 * When opened with an adapter that tracks its own quantities (e.g. WS
 * Order's local order state), prefills each row from that state instead of
 * the server-rendered default of 0 — so reopening a product already in the
 * buyer's order shows what they'd already set. No-op for the default
 * (real-cart) path, since that path has no such adapter.
 */
function applyInitialQuantities(root) {
  if (!activeAdapter?.getQuantity) return;

  root.querySelectorAll('.wholesale-modal__row').forEach((row) => {
    const variantId = row.dataset.variantId;
    if (!variantId) return;

    const quantity = activeAdapter.getQuantity(variantId);
    if (!quantity) return;

    const selector = row.querySelector('quantity-selector-component');
    if (selector && typeof selector.setValue === 'function') {
      selector.setValue(String(quantity));
      selector.updateButtonStates?.();
    }
  });

  updateTotals(root);
}

async function openWholesaleProductModal(productUrl, adapter) {
  activeAdapter = adapter || null;

  const dialogEl = getActiveElementById('wholesale-product-dialog');
  const contentMount = getActiveElementById('wholesale-product-modal-content');
  if (!(dialogEl instanceof WholesaleProductDialog) || !contentMount) return;

  contentMount.innerHTML = '<p class="wholesale-product-modal__loading">Loading…</p>';
  dialogEl.showDialog();

  try {
    const content = await fetchProductModalContent(productUrl);
    if (!content) {
      contentMount.innerHTML = '<p class="wholesale-product-modal__loading">Unable to load this product.</p>';
      return;
    }

    contentMount.replaceChildren(content);
    bindFinishSelector(contentMount);
    bindOrderingRows(contentMount);
    bindGalleryZoom(contentMount);
    applyInitialQuantities(contentMount);
  } catch (error) {
    console.error('Wholesale product modal: failed to load content', error);
    contentMount.innerHTML = '<p class="wholesale-product-modal__loading">Something went wrong loading this product.</p>';
  }
}

// open(productUrl, adapter?) — adapter is optional; omitting it keeps the
// original Lookbook behavior (writes straight to the real Shopify cart).
window.WholesaleProductModal = {
  open: openWholesaleProductModal,
};
